import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend,
  AreaChart, Area,
} from 'recharts'
import { relatoriosApi, categoriasApi, cartoesApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Card, CardBody, ProgressBar } from '../../components/ui/Card'
import { LoadingState, EmptyState } from '../../components/ui/EmptyState'
import { MonthNavigator, Tabs } from '../../components/ui/Navigation'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { TransactionType, ModalidadeCartao, StatusFatura, STATUS_FATURA_LABEL } from '../../lib/enums'
import { formatMoney } from '../../lib/utils'
import type { RelatorioCategoriasItem, Categoria } from '../../types'
import { ComparativoTab } from './ComparativoTab'
import { FaturaCategoriasPanel } from '../cartoes/FaturaCategorias'
import { Select } from '../../components/ui/Input'

type Tab = 'categoria' | 'fatura' | 'mensal' | 'historico' | 'comparativo' | 'projecao'

const TABS = [
  { key: 'categoria' as const, label: 'Por categoria', icon: 'ri-pie-chart-2-line' },
  { key: 'fatura' as const, label: 'Por fatura', icon: 'ri-bank-card-line' },
  { key: 'mensal' as const, label: 'Por mês', icon: 'ri-bar-chart-2-line' },
  { key: 'historico' as const, label: 'Categorias no tempo', icon: 'ri-line-chart-line' },
  { key: 'comparativo' as const, label: 'Comparativo', icon: 'ri-bar-chart-grouped-line' },
  { key: 'projecao' as const, label: 'Projeção', icon: 'ri-line-chart-line' },
]

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']

const CHART_COLORS = ['#FFB300', '#22C55E', '#3B82F6', '#EF4444', '#A78BFA', '#F97316', '#EC4899', '#14B8A6', '#84CC16', '#F59E0B']
const CHART_TOOLTIP = { background: '#1B2330', border: '1px solid #2D3748', borderRadius: 4, fontSize: 12 }
const AXIS_TICK = { fontSize: 11, fill: '#8B949E' }
const kFormatter = (v: number | string) => `${(Number(v) / 1000).toFixed(0)}k`

export function RelatoriosPage() {
  const toast = useToast()
  const now = new Date()
  const [mes, setMes] = useState(now.getMonth() + 1)
  const [ano, setAno] = useState(now.getFullYear())
  const [tab, setTab] = useState<Tab>('categoria')

  const { data: catData, isLoading: loadingCat } = useQuery({
    queryKey: ['relatorio-categorias', mes, ano],
    queryFn: () => relatoriosApi.porCategoria(mes, ano),
    enabled: tab === 'categoria',
  })
  // Sempre carregado: alimenta a aba "Por mês" e indica o último mês com movimento.
  const { data: mensalData = [], isLoading: loadingMensal } = useQuery({
    queryKey: ['relatorio-mensal', mes, ano],
    queryFn: () => relatoriosApi.porMes(mes, ano),
  })

  const ultimoMesComDados = [...mensalData].reverse().find((m) => m.receitas > 0 || m.despesas > 0)
  const { data: projecao = [], isLoading: loadingProjecao } = useQuery({
    queryKey: ['relatorio-projecao'],
    queryFn: () => relatoriosApi.projecao(6),
    enabled: tab === 'projecao',
  })

  const exportar = useMutation({
    mutationFn: () => relatoriosApi.exportarCsv(mes, ano),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `transacoes_${String(mes).padStart(2, '0')}_${ano}.csv`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('CSV exportado.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <>
      <PageHeader title="Relatórios" />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {tab !== 'comparativo' && tab !== 'historico' && tab !== 'fatura' && (
            <>
              <MonthNavigator mes={mes} ano={ano} onChange={(m, a) => { setMes(m); setAno(a) }} />
              <p className="text-13 text-muted"><i className="ri-information-line" aria-hidden /> Considera apenas lançamentos confirmados.</p>
            </>
          )}
        </div>
        <Button variant="soft-info" icon="ri-file-download-line" onClick={() => exportar.mutate()} loading={exportar.isPending}>Exportar CSV</Button>
      </div>

      <Card>
        <Tabs tabs={TABS} active={tab} onChange={setTab} className="px-4 pt-1" />
        <CardBody>
          {tab === 'categoria' && (
            loadingCat ? <LoadingState /> : catData && (
              catData.despesas.length === 0 && catData.receitas.length === 0 ? (
                <EmptyState
                  icon="ri-pie-chart-line"
                  message={`Nenhum lançamento confirmado em ${MESES[mes - 1]} de ${ano}.`}
                  action={ultimoMesComDados && (ultimoMesComDados.mes !== mes || ultimoMesComDados.ano !== ano) ? (
                    <Button variant="soft-primary" icon="ri-arrow-go-back-line" onClick={() => { setMes(ultimoMesComDados.mes); setAno(ultimoMesComDados.ano) }}>
                      Ver {ultimoMesComDados.label}, o último mês com movimento
                    </Button>
                  ) : undefined}
                />
              ) : (
                <div className="grid gap-6 xl:grid-cols-2">
                  <CategoryBreakdown title="Despesas por categoria" items={catData.despesas} emptyMessage="Sem despesas no período." />
                  <CategoryBreakdown title="Receitas por categoria" items={catData.receitas} emptyMessage="Sem receitas no período." />
                </div>
              )
            )
          )}

          {tab === 'fatura' && <PorFaturaTab />}

          {tab === 'mensal' && (
            loadingMensal ? <LoadingState /> : mensalData.length === 0 ? (
              <EmptyState icon="ri-bar-chart-2-line" message="Sem dados no período." />
            ) : (
              <>
                <h5 className="mb-4 text-15 font-medium text-white">Receitas × despesas nos últimos 12 meses</h5>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={mensalData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
                    <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={kFormatter} />
                    <Tooltip formatter={(v) => formatMoney(Number(v))} contentStyle={CHART_TOOLTIP} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#8B949E' }} iconType="circle" />
                    <Bar dataKey="receitas" name="Receitas" fill="#22C55E" radius={[3, 3, 0, 0]} maxBarSize={26} />
                    <Bar dataKey="despesas" name="Despesas" fill="#EF4444" radius={[3, 3, 0, 0]} maxBarSize={26} />
                  </BarChart>
                </ResponsiveContainer>
              </>
            )
          )}

          {tab === 'historico' && <CategoriaHistoricoTab />}

          {tab === 'comparativo' && <ComparativoTab />}

          {tab === 'projecao' && (
            loadingProjecao ? <LoadingState /> : projecao.length === 0 ? (
              <EmptyState icon="ri-line-chart-line" message="Sem recorrências ativas para projetar." />
            ) : (
              <>
                <h5 className="text-15 font-medium text-white">Saldo projetado para os próximos 6 meses</h5>
                <p className="mb-4 text-13 text-muted">Calculado a partir das recorrências ativas.</p>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={projecao} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="saldoFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FFB300" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#FFB300" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
                    <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={kFormatter} />
                    <Tooltip formatter={(v) => formatMoney(Number(v))} contentStyle={CHART_TOOLTIP} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#8B949E' }} iconType="circle" />
                    <Area type="monotone" dataKey="saldoProjetado" name="Saldo" stroke="#FFB300" strokeWidth={2.5} fill="url(#saldoFill)" dot={{ r: 3, fill: '#FFB300' }} />
                    <Area type="monotone" dataKey="receitasProjetadas" name="Receitas" stroke="#22C55E" strokeWidth={1.5} fill="none" strokeDasharray="4 3" />
                    <Area type="monotone" dataKey="despesasProjetadas" name="Despesas" stroke="#EF4444" strokeWidth={1.5} fill="none" strokeDasharray="4 3" />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded bg-border/60 sm:grid-cols-3 xl:grid-cols-6">
                  {projecao.map((p) => (
                    <div key={`${p.mes}-${p.ano}`} className="bg-surface2/60 p-3 text-center">
                      <p className="text-2xs uppercase text-muted">{p.label}</p>
                      <p className={`text-13 font-semibold ${p.saldoProjetado >= 0 ? 'text-income' : 'text-expense'}`}>{formatMoney(p.saldoProjetado)}</p>
                    </div>
                  ))}
                </div>
              </>
            )
          )}
        </CardBody>
      </Card>
    </>
  )
}

function PorFaturaTab() {
  const [cartaoId, setCartaoId] = useState('')
  const [faturaId, setFaturaId] = useState('')

  const { data: cartoes = [], isLoading: loadingCartoes } = useQuery({ queryKey: ['cartoes'], queryFn: cartoesApi.list })
  const credito = cartoes.filter((c) => c.modalidade === ModalidadeCartao.Credito)
  const cartaoAtual = credito.find((c) => c.id === cartaoId) ?? credito[0]

  const { data: faturas = [], isLoading: loadingFaturas } = useQuery({
    queryKey: ['faturas', cartaoAtual?.id],
    queryFn: () => cartoesApi.listFaturas(cartaoAtual!.id),
    enabled: !!cartaoAtual,
  })
  // Mais recente primeiro. Padrão: a fatura aberta mais próxima (a primeira aberta no tempo); sem aberta, a mais recente.
  const ordenadas = [...faturas].sort((a, b) => (b.ano - a.ano) || (b.mes - a.mes))
  const padrao = [...ordenadas].reverse().find((f) => f.status === StatusFatura.Aberta) ?? ordenadas[0]
  const faturaAtual = ordenadas.find((f) => f.id === faturaId) ?? padrao

  if (loadingCartoes) return <LoadingState />
  if (credito.length === 0) return <EmptyState icon="ri-bank-card-line" message="Nenhum cartão de crédito cadastrado." />

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Select label="Cartão" value={cartaoAtual?.id ?? ''} onChange={(e) => { setCartaoId(e.target.value); setFaturaId('') }}>
            {credito.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </Select>
        </div>
        <div className="w-72">
          <Select label="Fatura" value={faturaAtual?.id ?? ''} onChange={(e) => setFaturaId(e.target.value)} disabled={ordenadas.length === 0}>
            {ordenadas.map((f) => (
              <option key={f.id} value={f.id}>{MESES[f.mes - 1]} de {f.ano} · {STATUS_FATURA_LABEL[f.status]} · {formatMoney(f.valorTotal)}</option>
            ))}
          </Select>
        </div>
      </div>
      {loadingFaturas ? <LoadingState /> : !faturaAtual ? (
        <EmptyState icon="ri-file-list-3-line" message="Este cartão ainda não tem faturas." />
      ) : (
        <FaturaCategoriasPanel faturaId={faturaAtual.id} />
      )}
    </>
  )
}

function CategoriaHistoricoTab() {
  const [tipo, setTipo] = useState<TransactionType>(TransactionType.Expense)
  const [categoriaIds, setCategoriaIds] = useState<string[]>([])
  const [anual, setAnual] = useState(false)

  const { data: categorias = [] } = useQuery({ queryKey: ['categorias'], queryFn: () => categoriasApi.list({ incluirInativas: false }) })
  const todasCategorias = achatarCategorias(categorias)

  const { data, isLoading } = useQuery({
    queryKey: ['relatorio-categoria-historico', categoriaIds, tipo, anual],
    queryFn: () => relatoriosApi.categoriaHistorico(categoriaIds, tipo, anual),
    enabled: categoriaIds.length > 0,
  })

  function alternarCategoria(id: string) {
    setCategoriaIds((atual) => atual.includes(id) ? atual.filter((c) => c !== id) : [...atual, id])
  }

  function mudarTipo(novoTipo: TransactionType) {
    setTipo(novoTipo)
    setCategoriaIds([])
  }

  // Recharts precisa de um array de pontos únicos (por label) com uma chave por série.
  const labels = data?.series[0]?.pontos.map((p) => p.label) ?? []
  const dadosGrafico = labels.map((label, i) => {
    const linha: Record<string, string | number> = { label }
    for (const serie of data?.series ?? []) linha[serie.categoriaNome] = serie.pontos[i]?.valor ?? 0
    return linha
  })

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex overflow-hidden rounded border border-border/70">
          {([[TransactionType.Expense, 'Despesa'], [TransactionType.Income, 'Receita']] as const).map(([v, label]) => (
            <button
              key={v} type="button" onClick={() => mudarTipo(v)}
              className={`px-3 py-1.5 text-13 font-medium transition-colors ${tipo === v ? 'bg-primary/15 text-primary' : 'text-muted hover:text-white'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex overflow-hidden rounded border border-border/70">
          {([[false, 'Mensal'], [true, 'Anual']] as const).map(([v, label]) => (
            <button
              key={String(v)} type="button" onClick={() => setAnual(v)}
              className={`px-3 py-1.5 text-13 font-medium transition-colors ${anual === v ? 'bg-primary/15 text-primary' : 'text-muted hover:text-white'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="text-13 text-muted">{anual ? 'Últimos 5 anos' : 'Últimos 6 meses'}</p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {todasCategorias.filter((c) => c.transacoesCount > 0 || categoriaIds.includes(c.id)).map((c) => {
          const ativo = categoriaIds.includes(c.id)
          return (
            <button
              key={c.id} type="button" onClick={() => alternarCategoria(c.id)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-13 transition-colors ${ativo ? 'border-primary bg-primary/15 text-primary' : 'border-border/70 text-muted hover:text-white'}`}
            >
              {c.icone} {c.nome}
            </button>
          )
        })}
      </div>

      {categoriaIds.length === 0 ? (
        <EmptyState icon="ri-line-chart-line" message="Escolha uma ou mais categorias acima para acompanhar." />
      ) : isLoading ? (
        <LoadingState />
      ) : !data || data.series.every((s) => s.pontos.every((p) => p.valor === 0)) ? (
        <EmptyState icon="ri-line-chart-line" message="Sem movimentação para essas categorias no período." />
      ) : (
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={dadosGrafico} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
            <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={kFormatter} />
            <Tooltip formatter={(v) => formatMoney(Number(v))} contentStyle={CHART_TOOLTIP} />
            <Legend wrapperStyle={{ fontSize: 12, color: '#8B949E' }} iconType="circle" />
            {data.series.map((s, i) => (
              <Line key={s.categoriaId} type="monotone" dataKey={s.categoriaNome} name={s.categoriaNome}
                stroke={s.categoriaCor ?? CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2.5} dot={{ r: 3 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </>
  )
}

function achatarCategorias(categorias: Categoria[]): Categoria[] {
  return categorias.flatMap((c) => [c, ...achatarCategorias(c.subCategorias ?? [])])
}

function CategoryBreakdown({ title, items, emptyMessage }: { title: string; items: RelatorioCategoriasItem[]; emptyMessage: string }) {
  const total = items.reduce((s, i) => s + i.total, 0)
  return (
    <div className="rounded border border-border/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h5 className="text-15 font-medium text-white">{title}</h5>
        <span className="text-13 font-semibold text-white">{formatMoney(total)}</span>
      </div>
      {items.length === 0 ? (
        <p className="py-10 text-center text-13 text-muted">{emptyMessage}</p>
      ) : (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="mx-auto h-[180px] w-[180px] flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={items} dataKey="total" nameKey="categoriaNome" innerRadius={52} outerRadius={80} strokeWidth={0} paddingAngle={2}>
                  {items.map((item, i) => <Cell key={item.categoriaNome} fill={item.categoriaCor ?? CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatMoney(Number(v))} contentStyle={CHART_TOOLTIP} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="flex-1 space-y-2.5">
            {items.slice(0, 8).map((item, i) => (
              <li key={item.categoriaNome}>
                <div className="mb-1 flex items-center justify-between gap-2 text-13">
                  <span className="flex min-w-0 items-center gap-2 text-muted">
                    <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: item.categoriaCor ?? CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="truncate">{item.categoriaIcone} {item.categoriaNome}</span>
                  </span>
                  <span className="whitespace-nowrap font-medium text-white">{item.percentual.toFixed(0)}%</span>
                </div>
                <ProgressBar value={item.percentual} tone="muted" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
